import moment, { Moment } from "moment"
import { createSelector } from "reselect"
import { get } from "lodash"
import { ReduxState } from "model/state"
import { DATE_FORMAT, TIME_FORMAT_TZ, ONE_DAY, SAME_DAY, Period } from "./dates"
import { Uuid } from "./types/uuid"

export function generateIntradayBatchUrlAt(accountAlias: string, portfolioId: string | Uuid, asOf: string) {
  return `/api/${accountAlias}/p/${portfolioId}/intraday/batches/last-completed?asOf=${encodeURIComponent(asOf)}`
}

export const INTRADAY_LAST_BATCH_RESOURCE = "intradayLastBatch"

export const intradayLastBatchResource = (id: string) => `${INTRADAY_LAST_BATCH_RESOURCE}-${id}`

interface RegisteredQuery {
  url?: string | null
}

export function resolveIntradayLastBatchResource(
  channelQueries: Record<string, RegisteredQuery | undefined> | undefined | null,
  resourceId: string,
): string | null {
  if (!channelQueries) return null
  const perResource = intradayLastBatchResource(resourceId)
  if (channelQueries[perResource]) return perResource
  if (channelQueries[INTRADAY_LAST_BATCH_RESOURCE]) return INTRADAY_LAST_BATCH_RESOURCE
  return null
}

export function getIntradayBatchUrlAsOf(url: string | null | undefined): Moment | null {
  if (!url) return null
  const [, queryString] = url.split("?")
  if (!queryString) return null
  const asOf = new URLSearchParams(queryString).get("asOf")
  if (!asOf) return null
  const parsed = moment(asOf, moment.ISO_8601)
  return parsed.isValid() ? parsed : null
}

const getIntradayCurrentDataIdx = (state: ReduxState): number | null =>
  get(state, "global.intraday.refresh.currentDataIdx", null)

// Returns the instant captured when currentDataIdx last changed — frozen
// until the next tick (socket pulse / manual refresh) so all consumers
// share the same asOf and hit one cache entry. Without this, per-hook
// moment() calls would each generate a unique URL.
export const getIntradayBatchQueryAsOf = createSelector(
  [getIntradayCurrentDataIdx],
  () => moment().format(),
)

export type IntradayRelativeDay = "today" | "yesterday" | `${number} days ago`

export interface IntradayAsOfDisplay {
  currentAsOf: string
  intradayRelativeDay: IntradayRelativeDay
}

// batch.asOf is time-only (TIME_FORMAT_TZ) — moment(batch.asOf) alone is an Invalid Date.
// Combine with batch.date.
export function getIntradayBatchMoment(batch: { date: string, asOf: string }): Moment {
  return moment(`${batch.date} ${batch.asOf}`, `${DATE_FORMAT} ${TIME_FORMAT_TZ}`)
}

// Explicit reference day, not moment(asOf, TIME_FORMAT_TZ)'s implicit "today" — that can silently
// land on the wrong calendar day whenever local time and UTC currently disagree on what day it is.
export function getBatchMomentOrTimeOnly(
  batch: { date?: string | null, asOf?: string | null } | null | undefined,
  now: Moment = moment(),
): Moment | null {
  if (!batch?.asOf) return null
  const date = batch.date ?? now.format(DATE_FORMAT)
  return getIntradayBatchMoment({ date, asOf: batch.asOf })
}

// A committed batch can lag live pricing by its own commit cadence — a newer batch ID isn't necessarily a later moment.
export function isBatchNewerThanPricing(
  batch: { date?: string | null, asOf?: string | null } | null | undefined,
  pricingAsOf: string | null | undefined,
  now: Moment = moment(),
): boolean {
  const batchMoment = getBatchMomentOrTimeOnly(batch, now)
  if (!batchMoment) return false
  if (!pricingAsOf) return true
  return batchMoment.isAfter(moment(pricingAsOf))
}

export function computePricesAsOfDisplay(pricesAsOf: string | null | undefined, now: Moment = moment()): IntradayAsOfDisplay | null {
  if (!pricesAsOf) return null
  const pricesMoment = moment(pricesAsOf, moment.ISO_8601)
  if (!pricesMoment.isValid()) return null
  return {
    currentAsOf: pricesMoment.format("LTS"),
    intradayRelativeDay: relativeDayLabel(calendarDayDiff(pricesMoment.format(DATE_FORMAT), now)),
  }
}

export function relativeDayLabel(dayDiff: number): IntradayRelativeDay {
  if (dayDiff <= 0) return "today"
  if (dayDiff === 1) return "yesterday"
  return `${dayDiff} days ago`
}

// Compare two calendar dates by their DATE_FORMAT strings (not moment.format(), which converts to
// local tz and can shift the date for UTC-offset timezones when a timestamp is near midnight UTC).
export function calendarDayDiff(dateStr: string, now: Moment): number {
  return moment(now.format(DATE_FORMAT), DATE_FORMAT).diff(moment(dateStr, DATE_FORMAT), "days")
}

// Weekdays elapsed from dateStr (exclusive) through now (inclusive), skipping weekends.
function businessDayDiff(dateStr: string, now: Moment): number {
  const end = moment(now.format(DATE_FORMAT), DATE_FORMAT)
  const cursor = moment(dateStr, DATE_FORMAT)
  let count = 0
  while (cursor.isBefore(end)) {
    cursor.add(1, "day")
    if (cursor.isoWeekday() <= 5) count += 1
  }
  return count
}

export interface IntradayPeriodStaleness {
  isStale: boolean
  // Full text for the period-chip tooltip + the dot's accessible name.
  tooltipLabel: string
}

// Model-age staleness for the period chip: on a live intraday view, the single-day-anchored
// periods (Same Day, 1D) render live pricing on modeled data, so a latest modeled date two-plus
// business days back trips the dot. Measured from latestDate directly — the per-period store
// dates only coincide with the model's age for some periods.
export function getIntradayPeriodStaleness(
  period: Period | null | undefined,
  isIntradayMode: boolean,
  latestDate: string | null | undefined,
  modelName: string | null | undefined = null,
  now: Moment = moment(),
): IntradayPeriodStaleness {
  if (
    !isIntradayMode
    || (period !== SAME_DAY && period !== ONE_DAY)
    || !latestDate
    || businessDayDiff(latestDate, now) < 2
  ) {
    return { isStale: false, tooltipLabel: "" }
  }
  const dayDiff = calendarDayDiff(latestDate, now)
  const dateStr = moment(latestDate, DATE_FORMAT).format("M/D")
  const modelData = modelName ? `${modelName} data` : "Modeled data"
  const message = `Displayed Intraday analytics may be using stale data. ${modelData} is ${dayDiff} days old, from ${dateStr}.`
  return {
    isStale: true,
    tooltipLabel: message,
  }
}
